import { Component, inject, OnDestroy, viewChild } from '@angular/core';
import { AgentDetailsComponent } from './agent-details/agent-details.component';
import { AgentDefinitionComponent } from './agent-definition/agent-definition.component';
import { AgentApiSettingsComponent } from './agent-api-settings/agent-api-settings.component';
import { AgentSecurityComponent } from './agent-security/agent-security.component';

import { AgentBuilderService } from './services/agent-builder.service';
import { MatButtonModule } from '@angular/material/button';
import { IAgent, IAgentDetails } from '../../../../interfaces/i-agents';
import { AlertService } from '../../../../shared/alert/alert.service';
import { AlertEnum } from '../../../../shared/alert/alert.enum';
import { SpinnerService } from '../../../../shared/spinner/spinner.service';
import { ApiService } from '../../../../services/api.service';
import { NavItemLabelIds, RoutePath } from '../../../../enums/api.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { AgentService } from '../../services/agent.service';
import { ProfileService } from '../../../../services/profile.service';
import { mapLegacySensitivityLabel } from '../../../../utilities/sensitivity-label.utils';
import {
  SVGIcon,
  pencilIcon,
  fileHeaderIcon,
  gearsIcon,
  myspaceIcon,
  importIcon,
  dataJsonIcon,
  xOutlineIcon,
  checkOutlineIcon,
  lockIcon
} from "@progress/kendo-svg-icons";
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogConfirmComponent } from '../../../../shared/dialog-confirm/dialog-confirm.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AgentAdminSettingsComponent } from "./agent-admin-settings/agent-admin-settings.component";
import { firstValueFrom } from 'rxjs';
import { UiLayoutService } from '../../../../ui-layout/services/ui-layout.service';
import { UtilitiesService } from '../../../../services/utilities.service';
import { SensitivityLabelService } from '../../../../services/sensitivity-label.service';
import { UtilityStepperComponent } from '../../../../shared/utility-stepper/utility-stepper.component';
import { AgentStep } from '../../../../enums/agent.enum';
import { AGENT_STEP_SLUG_MAP, AGENT_STEP_TO_SLUG } from '../../constants/agent-step-slugs.constant';
import { MatIconModule } from '@angular/material/icon';
import { resolveStepSlugIndex } from "../../../../utilities/step-slug";

@Component({
  selector: 'app-agent-builder',
  imports: [
    ButtonsModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    AgentDetailsComponent,
    AgentDefinitionComponent,
    AgentApiSettingsComponent,
    AgentSecurityComponent,
    AgentAdminSettingsComponent,
    UtilityStepperComponent
],
  templateUrl: './agent-builder.component.html',
  styleUrls: [ './agent-builder.component.scss' ]
})
export class AgentBuilderComponent implements OnDestroy {
  agentDetailsComponents = viewChild(AgentDetailsComponent);
  agentDefinitionComponents = viewChild(AgentDefinitionComponent);
  agentApiSettingsComponents = viewChild(AgentApiSettingsComponent);
  agentSecurityComponents = viewChild(AgentSecurityComponent);
  agentAdminSettingsComponent = viewChild(AgentAdminSettingsComponent);

  selectedIndex = 0;
  title = 'Create Agent';
  itemName = '';
  agentId: string | null = null;
  isAgentOwner: boolean = false;
  formValidationErrorList: string[] = [];
  showExportButton = false;
  showImportButton = false;
  hasImportedAgent = false;

  // Stepper
  // Expose enum to template
  readonly AgentStep = AgentStep;

  // Check if user can manage workflows
  canManage = false;

  // Show admin step if user has admin rights
  showInstructionAdminStep = false;

  // Flag to control validation
  validateOnRender = false;

  // Define steps for the stepper with validation
  currentStep = 0;
  // Track validation status for each step - true = valid, false = invalid, null = not validated
  validationStatus: (boolean | null)[] = [];

  // SVG Icons
  readonly detailsIcon: SVGIcon = pencilIcon;
  readonly definitionIcon: SVGIcon = fileHeaderIcon;
  readonly apiSettingsIcon: SVGIcon = gearsIcon;
  readonly securityIcon: SVGIcon = lockIcon;
  readonly adminIcon: SVGIcon = myspaceIcon;
  readonly errorIcon: SVGIcon = xOutlineIcon;
  readonly successIcon: SVGIcon = checkOutlineIcon;
  readonly importSVG: SVGIcon = importIcon;
  readonly dataJsonSVG: SVGIcon = dataJsonIcon;

  // Initialize steps array with default values
  steps = [
    {
      label: AgentStep.DETAILS,
      svgIcon: this.detailsIcon,
      // Function to check if the step is valid, only when validation is requested
      isValid: (): boolean =>
        this.validateOnRender ? this.#validateCurrentStep() : true,
      validate: true,
    },
    {
      label: AgentStep.DEFINITION,
      svgIcon: this.definitionIcon,
      isValid: (): boolean =>
        this.validateOnRender ? this.#validateCurrentStep() : true,
      validate: true,
    },
    {
      label: AgentStep.API_SETTINGS,
      svgIcon: this.apiSettingsIcon,
      isValid: (): boolean =>
        this.validateOnRender ? this.#validateCurrentStep() : true,
      validate: false, // API Settings step doesn't have required fields, so no validation needed
    },
    {
      label: AgentStep.SECURITY,
      svgIcon: this.securityIcon,
      isValid: (): boolean =>
        this.validateOnRender ? this.#validateCurrentStep() : true,
      validate: false, // Security step doesn't have required fields, so no validation needed
    }
  ];

  readonly dialog = inject(MatDialog);
  profileService = inject(ProfileService);

  #agentBuilderService = inject(AgentBuilderService);
  #alertService = inject(AlertService);
  #spinnerService = inject(SpinnerService);
  #apiService = inject(ApiService);
  #agentService = inject(AgentService);
  #uiLayoutService = inject(UiLayoutService);
  #router = inject(Router);
  #route = inject(ActivatedRoute)
  #currentAgent = this.#agentBuilderService.agent();
  #profileService = inject(ProfileService);
  #utilitiesService = inject(UtilitiesService);
  #sensitivityLabelService = inject(SensitivityLabelService);

  constructor() {
    const userProfile = this.#profileService.userProfile()?.config;
    const canManage = userProfile?.canManageAgents ?? false;

    this.showExportButton = canManage;
    this.showImportButton = canManage;

    this.#route.params.subscribe(params => {
      if (params['agentId']) {
        // Get the agent ID from the URL
        const agentId = params['agentId'];
        this.agentId = agentId;

        // Only fetch from the API if we haven't already loaded this agent.
        if (this.#agentBuilderService.agent().id !== agentId) {

          this.#spinnerService.show();
          this.#apiService.getAgent(agentId).subscribe({
            next: agent => {
              this.#agentBuilderService.agent.set(agent);
              this.#currentAgent = agent;

              this.isAgentOwner = this.#agentBuilderService.agent().details.owners.some(owner => owner.userId === this.profileService.userProfile()!.profile.email || this.profileService.userProfile()!.config?.canManageAgents);
              if (this.#router.url.includes(RoutePath.CloneAgent)) {
                // Clone the agent
                this.#cloneAgent();
                this.#agentService.isEditAgent.set(false);
              }

              if (this.#router.url.includes(RoutePath.EditAgent)) {
                // Update the agent with the values from the selected agent
                this.title = `Edit Agent`;
                this.itemName = `: ${this.#currentAgent.details.name}`;
                this.#agentService.isEditAgent.set(true);
              }
            },
            error: err => {
              this.#alertService.showAlert({
                message: `Failed to retrieve agent. Please try again.`,
                type: AlertEnum.error,
              });
              // Reset the agent to the default values and set the currentAgent to the default values
              this.#agentBuilderService.resetAgent();
              this.#currentAgent = this.#agentBuilderService.agent();
              this.#agentService.isEditAgent.set(false);
              console.error(err);
            },
            complete: () => {
              this.#spinnerService.hide();
            }
          });
        } else {
          // Data already in service — set flags without re-fetching.
          if (this.#router.url.includes(RoutePath.EditAgent)) {
            this.title = `Edit Agent`;
            this.itemName = `: ${this.#currentAgent.details.name}`;
            this.#agentService.isEditAgent.set(true);
          }
        } // end: if (agent not yet loaded)
      } else if (!params['agentId'] && !this.hasImportedAgent) {
        // If there is no agentId in the URL and we didn't import an Agent, we are creating a new agent.
        // Reset the agent to the default values and set the currentAgent to the default values
        this.#currentAgent = this.#agentBuilderService.agent();
        // We are creating a new Agent so we are the owner of it.
        this.isAgentOwner = true;
      }

      // Resolve the initial step from the optional :step URL segment.
      // Only applicable for edit routes (edit-agent/:id/:step).
      if (params['agentId'] && this.#router.url.includes(RoutePath.EditAgent)) {
        const index = resolveStepSlugIndex(params['step'], AGENT_STEP_SLUG_MAP, this.steps);
        if (index !== -1) this.currentStep = index;
      }
    });

    // Initialize validation status array with nulls (not validated yet)
    this.validationStatus = new Array(this.steps.length).fill(null);

    // Update steps array if user can manage agents
    this.canManage = userProfile?.canManageAgents ?? false;
    if (this.canManage && userProfile?.isAdmin) {
      // Update admin step visibility
      this.showInstructionAdminStep = true;

      // Add admin step if not already present
      // Check if admin step already exists
      const adminStepExists = this.steps.some(
        (step) => step.label === AgentStep.ADMIN
      );
      if (!adminStepExists) {
        this.steps.push({
          label: AgentStep.ADMIN,
          svgIcon: this.adminIcon,
          isValid: (): boolean =>
            this.validateOnRender ? this.#validateCurrentStep() : true,
          validate: false, // Admin step doesn't have required fields, so no validation needed
        });

        // Add entry to validation status array
        this.validationStatus.push(null);
      }
    }
  }

  ngOnDestroy(): void {
    // Only reset if navigating away from the agent builder entirely.
    // When stepping between steps the component is destroyed/recreated but
    // the target URL still contains the builder path — preserve the signal.
    const targetUrl = this.#router.getCurrentNavigation()?.finalUrl?.toString() ?? '';
    const stayingInBuilder =
      targetUrl.includes(RoutePath.EditAgent) ||
      targetUrl.includes(RoutePath.CloneAgent) ||
      targetUrl.includes(RoutePath.CreateAgent);

    if (!stayingInBuilder) {
      this.reset(false);
    }
  }

  async saveAgent() {
    this.#spinnerService.show();

    // Update agent with current form values before validation
    await this.#updateAgent();

    // Validate all steps to update their icons
    const isValid = this.validateAllChildForms();

    if (!isValid) {
      this.#alertService.showAlert({
        message: `Invalid form. Correct the form and try again.`,
        type: AlertEnum.error,
      });
      this.#spinnerService.hide();
      return;
    }
    try {
      await this.#updateAgent();
      const response = await firstValueFrom(this.#apiService.saveUserAgent(this.#agentBuilderService.agent().details));

      // Now save the agent settings isDisabled and isUniversal
      const settingsResponse = await firstValueFrom(this.#apiService.saveAgentSettings({
        id: this.#agentBuilderService.agent().id,
        isDisabled: this.#agentBuilderService.agent().isDisabled,
        isUniversal: this.#agentBuilderService.agent().isUniversal
      }));

      this.#alertService.showAlert({
        message: `Your "${this.#agentBuilderService.agent().details.name}" was saved successfully.`,
        type: AlertEnum.success,
      });


      const agentsResponse = await firstValueFrom(this.#apiService.getAgents(this.#agentService?.getSensitivityLabelFromSession()));
      const updatedData = this.#agentService.convertAgentsToArray(agentsResponse);
      this.#agentService.agents.set(updatedData);

      // If there is no last route or current route is create-agent, then take the route to the My Agents page after saving an Agent.
      // Select the NavItem for My Agents on the left nav menu.
      // If there is no meaningful last route, redirect to My Agents.
      // "Not meaningful" means: empty, the app-load default (/start-conversation),
      // or the user opened a builder URL directly (edit/clone/create-agent).
      const noMeaningfulOrigin = this.#utilitiesService.isNoMeaningfulOrigin(
        RoutePath.EditAgent,
        RoutePath.CloneAgent,
        RoutePath.CreateAgent
      );

      if (noMeaningfulOrigin) {
        this.#uiLayoutService.setSelectedNavItem(NavItemLabelIds.MyAgents);
        this.#utilitiesService.locationFrom = `/${RoutePath.MyAgents}`;
      }

      this.#router.navigateByUrl(this.#utilitiesService.locationFrom); // Redirect to last location
    } catch (error) {
      this.#alertService.showAlert({
        message: `Failed to save "${this.#agentBuilderService.agent().details.name}".`,
        type: AlertEnum.error,
      });
      console.error('saveAgent', error);
    } finally {
      this.#spinnerService.hide();
    }
  }

  cancel(): void {
    this.#utilitiesService.goToLastLocation();
  }

  /** Updates `itemName` in real-time as the user edits the name field. */
  onItemNameChange(name: string): void {
    this.itemName = `: ${name}`;
  }

  reset(showAlert = true): void {
    this.validateOnRender = false;
    this.currentStep = 0;
    this.#agentBuilderService.resetAgent();
    this.#currentAgent = this.#agentBuilderService.agent();

    // Reset validation status
    this.validationStatus = new Array(this.steps.length).fill(null);

    // Reset step icons to their original icons
    this.steps.forEach((step, index) => {
      step.svgIcon = this.getOriginalIcon(step.label);
    });

    const allComponents = [
      ...(this.agentDetailsComponents() ? [this.agentDetailsComponents()!] : []),
      ...(this.agentDefinitionComponents() ? [this.agentDefinitionComponents()!] : []),
      ...(this.agentApiSettingsComponents() ? [this.agentApiSettingsComponents()!] : []),
      ...(this.agentSecurityComponents() ? [this.agentSecurityComponents()!] : []),
      ...(this.agentAdminSettingsComponent() ? [this.agentAdminSettingsComponent()!] : [])
    ];

    allComponents.forEach((component) => {
      const form = component.agentForm;

      Object.keys(form.controls).forEach(key => {
        if (key in this.#currentAgent.details) {
          if (key === 'sensitivityLabels') {
            form.controls[key].setValue(this.#currentAgent.details[key].map(
              (label: any) => label.id) || []);
            // Update the checkbox state in AgentDetailsComponent
            // const detailsComponent = this.agentDetailsComponents();
            // if (detailsComponent) {
            //   detailsComponent.updateCheckedKeys(form.controls[key].value);
            // }

          } else {
            form.controls[key].setValue((this.#currentAgent.details as any)[key]);
          }
        } else if (key in this.#currentAgent) {
          form.controls[key].setValue((this.#currentAgent as any)[key]);
        }
      });

      if (form) {
        Object.keys(form.controls).forEach((key) => {
          form.controls[key].reset();
        });
      }
    });

      if (showAlert) {
      this.#alertService.showAlert({
        message: 'Agent has been reset to default values.',
        type: AlertEnum.success,
      });
    }
  }

  /**
   * Update the agent with the current form values
   * @param json Optional JSON data to import
   * @param stepIndex Optional index to update only a specific step/component
   */
  async #updateAgent(json?: any, stepIndex?: number) {
    // UPDATE the agent with the current form values or imported JSON data
    const allComponents = [
      ...(this.agentDetailsComponents() ? [this.agentDetailsComponents()] : []),
      ...(this.agentDefinitionComponents() ? [this.agentDefinitionComponents()] : []),
      ...(this.agentApiSettingsComponents() ? [this.agentApiSettingsComponents()] : []),
      ...(this.agentSecurityComponents() ? [this.agentSecurityComponents()] : []),
      ...(this.agentAdminSettingsComponent() ? [this.agentAdminSettingsComponent()] : [])
    ];

    // If stepIndex is provided, only update that specific component
    const componentsToUpdate = stepIndex !== undefined && stepIndex >= 0 && stepIndex < allComponents.length
      ? [allComponents[stepIndex]]
      : allComponents;

    // Create a new object for immutability, using the latest agent data from the service
    let updatedAgent = { ...this.#agentBuilderService.agent(), details: { ...this.#agentBuilderService.agent().details } };

    componentsToUpdate.forEach((component) => {
      const form = component!.agentForm;
      if (json) {
        updatedAgent = { id: this.#agentBuilderService.agent().id, ...json, details: { ...json.details }};

        componentsToUpdate.forEach((component, index) => {
          const form = component!.agentForm;
          Object.keys(form.controls).forEach(key => {
            // Updates the form values with the JSON data
            if ((json as any)[key] !== undefined) {
              // Skip the details key since it'll be handled separately
              if (json[key] != 'details') {
                form.controls[key].setValue((json as any)[key]);
              }
            }

            // Check if the key exists in the agent details Interface
            if ((json.details as any)[key] !== undefined) {
              if (key === 'sensitivityLabels' && json.details[key] != null) {
                this.#updateSensitivityLabelsFromJson(json.details);
                // Update the checkbox state in AgentDetailsComponent
                const detailsComponent = this.agentDetailsComponents();
                if (detailsComponent) {
                  detailsComponent.updateCheckedKeys(form.controls[key].value);
                }
              } else {
                form.controls[key].setValue((json.details as any)[key]);
              }
            }
          });
        });
      } else {
        // Update the agent values with the form values
        Object.keys(form.controls).forEach(key => {
          let value: any;

          switch (key) {
            case 'definition':
              try {
                const definition = JSON.parse(form.controls[key].value);
                // Format the JSON string with indentation (2 spaces)
                value = JSON.stringify(definition, null, 2);
              } catch (error) {
                this.#alertService.showAlert({
                  message: 'Invalid JSON format. Please correct the JSON and try again.',
                  type: AlertEnum.error,
                });
                console.error('Invalid JSON format in definition:', error);
              }
              break;
            case 'description':
            case 'name':
            case 'supportUrl':
            case 'systemMessage':
            case 'apiUrl':
            case 'apiHeaders':
            case 'apiPayload':
              value = form.controls[key].value ? form.controls[key].value.trim() : form.controls[key].value;
              break;
            case 'sensitivityLabels':
              if (form.controls[key].value != null) {
                value = this.#utilitiesService.findLabelsByCodes(form.controls[key].value);
              }
              // Update the checkbox state in AgentDetailsComponent
              const detailsComponent = this.agentDetailsComponents();
              if (detailsComponent) {
                detailsComponent.updateCheckedKeys(form.controls[key].value);
              }
              break;
            case 'keywords':
            case 'groups':
              value = form.controls[key].value ?? [];
              break;
            default:
              value = form.controls[key].value;
              break;
          }

          if (key in updatedAgent.details) {
            (updatedAgent.details as any)[key] = value;
          } else {
            if (this.#checkKeyExists(key)) {
              (updatedAgent as any)[key] = value;
            }
          }
        });
      }
    });

    // Flatten the details into the main agent object
    updatedAgent = { ...updatedAgent, ...updatedAgent.details };

    // Update the agent in the service with the new object
    this.#agentBuilderService.agent.set(updatedAgent);

    this.#currentAgent = this.#agentBuilderService.agent();

    if (json) {
      this.itemName = `: ${this.#currentAgent.details.name}`;
      this.#alertService.showAlert({
        message: 'Agent data has been successfully imported. Please save the Agent to apply changes.',
        type: AlertEnum.success,
      });
    }
    // END UPDATE the agent with the current form values
  }

  /**
   * Check if the key exists in the agent details Interface
   */
  #checkKeyExists(keyName: string): boolean {
    const agentDetails: IAgent = this.#agentBuilderService.createAgentDefaultValue();
    return keyName in agentDetails || keyName in agentDetails.details;
  }

  /**
   * Validate all child forms and return true if all forms are valid
   * @param stepName Optional step name to validate a specific step
   * @returns boolean indicating if the forms are valid
   */
  validateAllChildForms(stepName?: string): boolean {
    // Define the steps to validate, each with its component, validation check, and alert message
    const steps = [
      {
        step: AgentStep.DETAILS,
        component: this.agentDetailsComponents,
        serviceCheck: () => {
          // Check if agent details (name, description, supportUrl, sensitivity labels) are present and valid
          const details = this.#agentBuilderService.agent().details;
          const hasName = details?.name ? true : false;
          const hasDescription = details?.description ? true : false;
          // Sensitivity labels are only required when the feature is enabled; otherwise the picker is hidden.
          const sensitivityLabelsSatisfied = !this.#sensitivityLabelService.enabled()
            || (details?.sensitivityLabels && details.sensitivityLabels.length > 0);
          return hasName && hasDescription && sensitivityLabelsSatisfied;
        },
        alertMsg: 'Please correct the errors in the Agent Details step before proceeding.'
      },
      {
        step: AgentStep.DEFINITION,
        component: this.agentDefinitionComponents,
        serviceCheck: () => {
          // Check if agent definition (system message, definition) are present and valid
          const details = this.#agentBuilderService.agent().details;
          return details?.definition ? true : false;
        },
        alertMsg: 'Please correct the errors in the Agent Definition step before proceeding.'
      },
      {
        step: AgentStep.API_SETTINGS,
        component: this.agentApiSettingsComponents,
        serviceCheck: () => {
          // Check API settings if API is enabled
          const details = this.#agentBuilderService.agent().details;
          if (details?.usesApi) {
            return details?.apiUrl ? true : false;
          }
          return true;
        },
        alertMsg: 'Please correct the errors in the API Settings step before proceeding.'
      },
      {
        step: AgentStep.SECURITY,
        component: this.agentSecurityComponents,
        serviceCheck: () => {
          // Security step is always valid as it has optional fields
          return true;
        },
        alertMsg: 'Please correct the errors in the Security step before proceeding.'
      }
      // Admin step is always valid as it has optional fields
    ];

    // Iterate through each step and validate
    for (let i = 0; i < steps.length; i++) {
      const { step, component, serviceCheck, alertMsg } = steps[i];
      // Check if the current step matches the provided stepName or if no stepName is provided
      // This allows validation of all steps if stepName is not specified, else a single step.
      if (stepName === step || !stepName) {
        // If the form is invalid and the service check fails, show alert and set current step
        if (!component()?.isValid?.()) {
          if (!serviceCheck()) {
            this.currentStep = i;
            this.steps[i].svgIcon = this.errorIcon; // Set error icon for the step
            // Mark all controls as touched to show validation errors
            if (component() && component()!.agentForm) {
              component()?.markFormAsTouched(); // Mark all controls as touched to show validation errors
            }
            this.#alertService.showAlert({ message: alertMsg, type: AlertEnum.error });
            return false;
          }
        } else {
          this.steps[i].svgIcon = this.successIcon; // Set success icon if valid
        }
      }
    }
    // All steps are valid
    return true;
  }

  exportAgentToJson() {
    // get the latest values for the exported agent
    this.#updateAgent();
    this.#currentAgent = this.#agentBuilderService.agent();

    // check if the form data is valid.
    // if not, display a list of action items to fix the issue.
    // convert form data to json format.
    // export the json data to a file.
    // download the file.
    // setup a notification to the user that the file has been downloaded.
    // if the file hasn't been downloaded, then show an error message.
    // with an action list to fix the issue to make it be a valid form data to download.

    // Validate all forms before export
    const isValid = this.validateAllChildForms();

    if (!isValid) {
      this.#alertService.showAlert({
        message: `Invalid form. Correct the form and try again.`,
        type: AlertEnum.error,
      });

      this.openActionItemDialog();
      return;
    }

    // Create the JSON data matching the format of the provided JSON context using the current agent
    const jsonData: any = {
      id: this.#currentAgent.id,
      isDisabled: this.#currentAgent.isDisabled,
      isUniversal: this.#currentAgent.isUniversal,
      details: {
        ...this.#currentAgent.details
      }
    };

    const blob = new Blob([ JSON.stringify(jsonData, null, 2) ], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agent-data.json';
    a.click();
    window.URL.revokeObjectURL(url);

    this.#alertService.showAlert({
      message: 'Agent has been successfully exported.',
      type: AlertEnum.success,
    });
  }

  #importAgentFromJson() {
    // Show a notification warning users that if the agent has the same name, it will be overwritten.
    // And to back up and save the agent before continuing. Let it be a modal with a continue or cancel button.
    // Import the JSON data from the file.
    // Open the file dialog to select the file, with file type set to JSON.
    // Read the file data.
    // Convert the file data to form data.
    // Update the form data with the file data.
    // Show a notification to the user that the file has been imported.
    // If the file hasn't been imported, then show an error message.

    // created this flag to prevent resetting the agent to default values when importing an agent due to the effect() in the constructor being triggered by the
    // agentBuilderService agent() signal being updated.
    this.hasImportedAgent = true;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          try {
            const json: IAgent = JSON.parse(e.target.result);
            // Process the JSON data and update the form
            // TODO update this check for when we do bulk import
            // Check if the JSON data is an array, if so show an error message.
            if (Array.isArray(json)) {
              this.formValidationErrorList.push('Invalid JSON format. Only one agent can be imported at a time.');
              this.openActionItemDialog();
              return;
            }

            // Check if the JSON data is valid, if not show a list of action items to fix the issue.
            if (!this.#validateJsonData(json)) {
              this.openActionItemDialog();
              return;
            }

            const tempAgent = this.#agentService.agents().agents.find(agent => agent.details.id === json.id);
            const isSameAgentId = !!tempAgent;

            // Check if the Agent ID already exists in the list of Agents
            if (isSameAgentId) {
              // if the user is the owner of the Agent,
              // we give a confirmation dialog to let user know that an ID exists for this Agent
              // and ask the user if they want to clone the Agent.
              // if the user is not the owner of the Agent,
              // we display a dialog to let the user know that the ID exists and they cannot update it. Then ask if they would like to create a new Agent.
              // if so, prepend (Clone) to the name and give it a new ID while adding user to the list of owners.

              // make a check if the user is the owner of the Agent
              const isUserAnAgentOwner = tempAgent.details.owners.some(owner => owner.userId === this.profileService.userProfile()!.profile.email);

              if (isUserAnAgentOwner || !!this.profileService.userProfile()!.config?.canManageAgents) {
                this.openActionItemDialog({
                  message: `An Agent with the same ID exists, would you like to clone it?`,
                  buttonLabel: 'Overwrite',
                  title: 'Agent ID Exists',
                  showSecondaryButton: true,
                  showTertiaryButton: true,
                  tertiaryButtonLabel: 'Clone'
                }, undefined, () => this.#cloneAgent(true));
              } else {
                this.openActionItemDialog({
                  message: `An Agent with this ID exists and you cannot update it. Would you like to create a new Agent? Note: This will append '(Clone)' to the name of the new Agent.`,
                  buttonLabel: 'Yes',
                  title: 'Agent ID Exists',
                  showSecondaryButton: true,
                  showTertiaryButton: false,
                  tertiaryButtonLabel: ''
                }, () => this.#cloneAgent(true));
              }
            }

            // Update the form data with the imported JSON data
            this.#updateAgent(json);
          } catch (error) {
            console.error('Invalid JSON file:', error);
            this.#alertService.showAlert({
              message: 'Invalid JSON file. Please select a valid JSON file.',
              type: AlertEnum.error,
            });
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }

  #validateJsonData(json: IAgent): boolean {
    // reset the formValidationErrorList
    this.formValidationErrorList = [];

    const requiredAgentFields: (keyof IAgent)[] = [
      'id', 'details', 'isDisabled', 'isUniversal'
    ];

    const requiredAgentDetailsFields: (keyof IAgentDetails)[] = [
      'id', 'name', 'users', 'apiUrl', 'groups', 'owners', 'usesApi', 'usesGet', 'isEnabled',
      'apiHeaders', 'apiPayload', 'definition', 'supportUrl', 'description', 'sendUserInfo',
      'sendMessageId', 'sendSessionId', 'systemMessage', 'sendUserPrompt', 'sensitivityLabels',
      'requestManipulationCode', 'responseManipulationCode'
    ];

    this.formValidationErrorList.push('Invalid JSON format. Issues found:');

    // Check if all required fields are present
    for (const field of requiredAgentFields) {
      if (!(field in json)) {
        this.formValidationErrorList.push(`Missing required field: ${field}`);
      } else {
        switch (field) {
          case 'id':
            if (typeof json[field] !== 'string') {
              this.formValidationErrorList.push(`Field ${field} should be a string.`);
            }
            break;
          case 'details':
            if (typeof json[field] !== 'object') {
              this.formValidationErrorList.push(`Field ${field} should be an object.`);
            }
            break;
          case 'isDisabled':
            if (typeof json[field] !== 'boolean') {
              this.formValidationErrorList.push(`Field ${field} should be a boolean.`);
            }
            break;
          case 'isUniversal':
            if (typeof json[field] !== 'boolean') {
              this.formValidationErrorList.push(`Field ${field} should be a boolean.`);
            }
            break;
          default:
            this.formValidationErrorList.push(`Unknown field: ${field}`);
            break;
        }
      }
    }

    // if we don't have the details field, then we can't validate the rest of the fields.
    if (!json.details) {
      // this prevents dupe error messages for missing details.
      if (!this.formValidationErrorList.includes('Missing required field: details')) {
        this.formValidationErrorList.push('Missing required field: details');
      }
      return false;
    } else {
      for (const field of requiredAgentDetailsFields) {
        if (!(field in json.details)) {
          this.formValidationErrorList.push(`Missing required field: ${field}`);
        }
      }
    }

    // Helper function to validate the type of each field
    const validateField = (field: keyof IAgentDetails, type: string | string[]) => {
      const value = json.details[field];
      if (Array.isArray(type)) {
        return type.includes(typeof value);
      }
      return typeof value === type;
    };

    // Validate the types of each field
    const typeChecks: [ keyof IAgentDetails, string | string[] ][] = [
      [ 'id', 'string' ],
      [ 'name', 'string' ],
      [ 'users', 'object' ],
      [ 'apiUrl', 'string' ],
      [ 'groups', 'object' ],
      [ 'owners', 'object' ],
      [ 'usesApi', 'boolean' ],
      [ 'usesGet', 'boolean' ],
      [ 'isEnabled', 'boolean' ],
      [ 'apiHeaders', [ 'string', 'object' ] ],
      [ 'apiPayload', [ 'string', 'object' ] ],
      [ 'definition', 'string' ],
      [ 'supportUrl', 'string' ],
      [ 'description', 'string' ],
      [ 'sendUserInfo', 'boolean' ],
      [ 'sendMessageId', 'boolean' ],
      [ 'sendSessionId', 'boolean' ],
      [ 'systemMessage', [ 'string', 'object' ] ],
      [ 'sendUserPrompt', 'boolean' ],
      [ 'sensitivityLabels', 'object' ],
      [ 'requestManipulationCode', [ 'string', 'object' ] ],
      [ 'responseManipulationCode', [ 'string', 'object' ] ]
    ];

    for (const [ field, type ] of typeChecks) {
      if (!validateField(field, type)) {
        this.formValidationErrorList.push(`Type mismatch for field: ${field}`);
      }
    }

    // Validate the structure of users, owners, and sensitivityLabels
    const validateNestedObject = (obj: any, structure: { [key: string]: string }) => {
      for (const key in structure) {
        if (typeof obj[key] !== structure[key]) {
          return false;
        }
      }
      return true;
    };

    const userStructure = { name: 'string', userId: 'string' };
    const labelStructure = { id: 'number', label: 'string' };

    for (const user of json.details.users) {
      if (!validateNestedObject(user, userStructure)) {
        this.formValidationErrorList.push('User structure is incorrect');
        break;
      }
    }

    for (const owner of json.details.owners) {
      if (!validateNestedObject(owner, userStructure)) {
        this.formValidationErrorList.push('Owner structure is incorrect');
        break;
      }
    }

    // in case someone exports a legacy Agent with no sensitivityLabels
    if (json.details.sensitivityLabels != null) {
      for (const label of json.details.sensitivityLabels) {
        if (!validateNestedObject(label, labelStructure)) {
          this.formValidationErrorList.push('Sensitivity label structure is incorrect');
          break;
        }
      }
    }

    if (this.formValidationErrorList.length > 1) {
      console.error('Invalid JSON format:', this.formValidationErrorList);
      return false;
    }

    return true;
  }

  openImportDialog() {
    const dialogConfig = new MatDialogConfig();
    let messageContent = '';
    if (this.title === 'Edit Agent') {
      messageContent = `The import will overwrite any existing fields in the form.\n`;
    }
    messageContent += `If the agent has the same name, it will be overwritten. Make sure to back up and save the agent before continuing.\n`;

    dialogConfig.data = {
      message: messageContent,
      buttonLabel: 'Continue',
      title: 'Confirm',
      showSecondaryButton: true,
      showTertiaryButton: false,
      tertiaryButtonLabel: ''
    };
    dialogConfig.disableClose = true;

    const dialogRef = this.dialog.open(DialogConfirmComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.#importAgentFromJson();
      }
    });
  }

  openActionItemDialog(customData?: {
                         message: string,
                         buttonLabel: string,
                         title: string,
                         showSecondaryButton?: boolean,
                         showTertiaryButton?: boolean,
                         tertiaryButtonLabel?: string
                       },
                       primaryButtonCallback?: () => void,
                       tertiaryButtonCallback?: () => void) {
    const dialogConfig = new MatDialogConfig();
    let messageContent = '';
    if (customData) {
      dialogConfig.data = customData;
    } else {
      messageContent += `Please correct the following errors to export/import this Agent: \n ${this.formValidationErrorList.join('\n')}`;
      dialogConfig.data = {
        message: messageContent,
        buttonLabel: 'OK',
        title: 'Action Items'
      };
    }

    dialogConfig.disableClose = true;

    const dialogRef = this.dialog.open(DialogConfirmComponent, dialogConfig);

    if (primaryButtonCallback) {
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          primaryButtonCallback();
          dialogRef.close();
        }
      });
    }

    if (tertiaryButtonCallback) {
      dialogRef.componentInstance.tertiaryButtonClick.subscribe(() => {
        tertiaryButtonCallback();
        dialogRef.close();
      });
    }
  }

  async #cloneAgent(shouldSaveAndRedirect: boolean = false) {
    this.title = 'Clone Agent';
    const id = crypto.randomUUID();
    this.#agentBuilderService.agent.set({
      ...this.#agentBuilderService.agent(),
      id: id,
      details: {
        ...this.#agentBuilderService.agent().details,
        id: id,
        name: `(Clone) ${this.#agentBuilderService.agent().details.name}`,
      },
    });
    this.#currentAgent = this.#agentBuilderService.agent();
    this.itemName = `: ${this.#currentAgent.details.name}`;

    if (shouldSaveAndRedirect) {
      try {
        const response = await firstValueFrom(this.#apiService.saveUserAgent(this.#agentBuilderService.agent().details));

        // If there is no meaningful last route, redirect to My Agents.
        const noMeaningfulOriginSecondary = this.#utilitiesService.isNoMeaningfulOrigin(
          RoutePath.EditAgent,
          RoutePath.CloneAgent
        );

        if (noMeaningfulOriginSecondary) {
          this.#utilitiesService.locationFrom = `/${RoutePath.MyAgents}`;
        }

        this.#router.navigateByUrl(this.#utilitiesService.locationFrom); // Redirect to last location
      } catch (error) {
        this.#alertService.showAlert({
          message: `Failed to clone "${this.#agentBuilderService.agent().details.name}".`,
          type: AlertEnum.error,
        });
        console.error('cloneAgent', error);
      }
    }
  }

  #updateSensitivityLabelsFromJson(json: IAgentDetails) {
    const agentDetailsComponent = this.agentDetailsComponents();
    if (agentDetailsComponent) {
      const form = agentDetailsComponent.agentForm;
      if (form.controls['sensitivityLabels']) {
        // Map backend ISensitivityLabel integer IDs to the new GUID-based ids for the TreeView
        const sensitivityLabelGuids = json.sensitivityLabels
          .map(label => mapLegacySensitivityLabel(label)?.id)
          .filter((id): id is string => !!id);
        form.controls['sensitivityLabels'].setValue(sensitivityLabelGuids);
        agentDetailsComponent.updateCheckedKeys(sensitivityLabelGuids);
      }
    }
  }

    /**
   * Handles the activate event from Kendo stepper
   * This method is called before the step is changed and can prevent navigation
   * @param event The event containing the requested step index
   */
    onStepActivate(event: any) {
      // Enable validation only when needed for navigation
      this.validateOnRender = true;

      // Validate the current step
      const isCurrentStepValid = this.#validateCurrentStep();

      if (!isCurrentStepValid) {
        // Show validation errors
        this.#alertService.showAlert({
          message: `Please correct the validation errors before proceeding.`,
          type: AlertEnum.error,
        });

        // Prevent navigation by returning false
        event.preventDefault();
      }
    }

    /**
     * Handles step change in the stepper
     * @param index Numeric index of the selected step (0: Definition, 1: Types, 2: Admin)
     */
    onStepChange(index: number): void {
      // Update instruction with current form values so they are saved before changing step
      // Pass the current step index to only update that component's data
      this.#updateAgent(undefined, this.currentStep);

      // Reset icon for the previous step if it was validated and needs validation
      if (
        this.steps[this.currentStep].validate &&
        this.validationStatus[this.currentStep] !== null
      ) {
        // If the step was validated, update its icon based on validation result
        const isValid = this.validationStatus[this.currentStep];
        this.steps[this.currentStep].svgIcon = isValid
          ? this.successIcon
          : this.errorIcon;
      }

      // The step change will only happen if onStepActivate returned true
      // So we can safely update the current step
      this.currentStep = index;

      // Sync the URL step slug so the active step is bookmarkable / shareable.
      this.#syncStepToUrl(index);
    }

    /**
     * Updates the URL to reflect the currently active step slug, enabling bookmarking and sharing.
     * Replaces the `:step` segment without adding a new browser history entry.
     */
    #syncStepToUrl(index: number): void {
      // Step-slug URLs are only supported for edit routes (edit-agent/:id/:step).
      if (!this.agentId || !this.#router.url.includes(RoutePath.EditAgent)) {
        return;
      }

      const stepLabel = this.steps[index]?.label as AgentStep | undefined;
      const slug = stepLabel ? AGENT_STEP_TO_SLUG[stepLabel] : undefined;
      if (!slug) {
        return;
      }

      const urlSegments = this.#router.url.split('/');
      // urlSegments: ['', 'edit-agent', ':id', ':step?']
      // Replace or append the step slug at index 3.
      urlSegments[3] = slug;

      this.#router.navigateByUrl(urlSegments.join('/'), { replaceUrl: true });
    }

    /**
     * Validates the current step before moving to the next
     * @returns boolean indicating if the current step is valid
     */
    #validateCurrentStep(): boolean {
      let isValid = false;

      // Use numeric indices for the steps to avoid enum comparison issues
      switch (this.currentStep) {
        case 0: // Agent Details step
          isValid = this.validateAllChildForms(AgentStep.DETAILS);
          break;
        case 1: // Agent Definition step
          isValid = this.validateAllChildForms(AgentStep.DEFINITION);
          break;
        case 2: // API Settings step
          isValid = this.validateAllChildForms(AgentStep.API_SETTINGS);
          break;
        case 3: // Security step
          isValid = this.validateAllChildForms(AgentStep.SECURITY);
          break;
        default:
          isValid = true;
          break;
      }

      return isValid;
    }

      /**
   * Handles validation status changes from the stepper component
   * @param status The updated validation status array
   */
    onValidationStatusChange(status: (boolean | null)[]): void {
      this.validationStatus = status;

      // Apply appropriate icons based on validation status
      this.steps.forEach((step, index) => {
        if (index < status.length) {
          if (status[index] === true) {
            // Valid - set success icon
            step.svgIcon = this.successIcon;
          } else if (status[index] === false) {
            // Invalid - set error icon
            step.svgIcon = this.errorIcon;
          } else {
            // Not validated or null - reset to original icon
            step.svgIcon = this.getOriginalIcon(step.label);
          }
        }
      });
    }

    /**
     * Gets the original icon for a given step label
     * @param label The step label to get the icon for
     * @returns The original SVG icon for the step
     */
    getOriginalIcon(label: string): SVGIcon {
      switch (label) {
        case AgentStep.DETAILS:
          return this.detailsIcon;
        case AgentStep.DEFINITION:
          return this.definitionIcon;
        case AgentStep.API_SETTINGS:
          return this.apiSettingsIcon;
        case AgentStep.SECURITY:
          return this.securityIcon;
        case AgentStep.ADMIN:
          return this.adminIcon;
        default:
          return this.detailsIcon;
      }
    }
}
